import React from 'react';
import PropTypes from 'prop-types';

import backend from 'utils/backend';
import {dispatch, connect} from 'utils/state_manager';

import moment from 'moment';

import {Box, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import TaskItem from './components/TaskItem';
import TaskListSkeleton from './components/TaskListSkeleton';
import {TaskModal} from 'views/module';

const MyDay = (props) => {
  const {
    user,
    taskRefresh,
    taskRefreshBump,
    statusProgressWrap: progressWrap,
  } = props;

  const [todaysTasks, setTodaysTasks] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [model, setModel] = React.useState({});
  const [clientNameByRn, setClientNameByRn] = React.useState({});

  const processTaskList = (taskList) => {
    const startOfTodayUtc = moment().startOf('day').utc();
    const endOfTodayUtc = moment().endOf('day').utc();

    const sortedTasksWithDueDate = taskList
        .filter((task) => {
          const dueDate = moment(task.dueDate, moment.ISO_8601, true);
          if (!dueDate.isValid()) {
            return false;
          }

          return dueDate.isBefore(startOfTodayUtc) ||
            dueDate.isBetween(startOfTodayUtc, endOfTodayUtc, null, '[]');
        })
        .sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf());

    return sortedTasksWithDueDate;
  };

  const loadTasks = React.useCallback(() => {
    if (!user?.userRn) {
      setTodaysTasks([]);
      setClientNameByRn({});
      return;
    }

    setIsLoading(true);

    progressWrap(async () => {
      try {
        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const [userTaskResponse, clientResponse] = await Promise.all([
          backend.workflowEngine.getUserTasks(user.userRn),
          backend.client.queryClient([], sortBy),
        ]);
        const userTasks = userTaskResponse?.list || [];
        const clientMap = (clientResponse?.list ?? []).reduce((acc, client) => {
          if (client?.clientRn) {
            acc[client.clientRn] = client.clientName || client.clientRn;
          }
          return acc;
        }, {});
        const processedTaskList = processTaskList(userTasks);
        setClientNameByRn(clientMap);
        setTodaysTasks(processedTaskList);
      } finally {
        setIsLoading(false);
      }
    });
  }, [user?.userRn, progressWrap, taskRefresh]);

  const todayTasksIncomplete = React.useMemo(() => todaysTasks.filter((task) => task.status !== 'COMPLETE'), [todaysTasks]);

  const todayTasksComplete = React.useMemo(() => todaysTasks.filter((task) => task.status === 'COMPLETE'), [todaysTasks]);

  const dueTodayCount = React.useMemo(() => todayTasksIncomplete.length, [todayTasksIncomplete]);

  const isTaskAssignedToUser = (task, userRn) => {
    const ownerRns = Array.isArray(task?.ownerRns) ?
      task.ownerRns :
      (task?.ownerRn ? [task.ownerRn] : []);

    return Boolean(userRn) && ownerRns.includes(userRn);
  };

  const isDueToday = (dueDate) => {
    const dueDateMoment = moment(dueDate, moment.ISO_8601, true);
    return dueDateMoment.isValid() && dueDateMoment.isSame(moment(), 'day');
  };

  const handleSetStatusToComplete = React.useCallback((taskRn, isComplete) => {
    if (isComplete) return;

    setIsLoading(true);

    progressWrap(async () => {
      try {
        const updatedTask = await backend.workflowEngine.updateTaskStatus(taskRn, 'COMPLETE');
        setTodaysTasks((previousTasks) => {
          const existingTask = previousTasks.find((task) => task.taskRn === taskRn);
          const mergedTask = {
            ...(existingTask || {}),
            ...(updatedTask && typeof updatedTask === 'object' ? updatedTask : {}),
            taskRn,
            status: 'COMPLETE',
          };

          return processTaskList([
            ...previousTasks.filter((task) => task.taskRn !== taskRn),
            mergedTask,
          ]);
        });
      } finally {
        setIsLoading(false);
      }
    });
  }, [progressWrap]);

  React.useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleAddTask = (taskData) => {
    progressWrap(async () => {
      try {
        const createdTask = await backend.workflowEngine.addTask(taskData);
        const shouldAppend = isTaskAssignedToUser(createdTask, user?.userRn) &&
          isDueToday(createdTask.dueDate);

        if (shouldAppend) {
          setTodaysTasks((previousTasks) =>
            processTaskList([
              ...previousTasks.filter((task) => task.taskRn !== createdTask.taskRn),
              createdTask,
            ]),
          );
        }
      } finally {
        setModel({});
      }
    });
  };

  const openDialogs = {
    close: () => {
      setModel({});
    },
    addTask: () => {
      setModel({action: 'ADD_TASK'});
    },
    editTask: (task) => {
      setModel({action: 'EDIT_TASK', task});
    },
  };

  const edits = {
    editTask: (taskRn, taskData) => {
      progressWrap(async () => {
        try {
          await backend.workflowEngine.updateTask(taskRn, taskData);
          taskRefreshBump();
        } finally {
          setModel({});
          loadTasks();
        }
      });
    },
    removeTask: (taskData) => {
      progressWrap(async () => {
        try {
          await backend.workflowEngine.removeTask(taskData.taskRn);
          setTodaysTasks((previousTasks) =>
            previousTasks.filter((task) => task.taskRn !== taskData.taskRn),
          );
        } finally {
          setModel({});
        }
      });
    },
  };

  const popModelGenerator = () => {
    let popModel = null;

    switch (model.action) {
      case 'ADD_TASK':
        popModel = (
          <TaskModal
            mode='add'
            onClose={openDialogs.close}
            addTask={handleAddTask}
            askConnections
          />
        );
        break;
      case 'EDIT_TASK':
        popModel = (
          <TaskModal
            mode='edit'
            onClose={openDialogs.close}
            editTask={edits.editTask}
            task={model.task}
            askStatus
            askConnections
          />
        );
        break;
    }

    return popModel;
  };

  return (
    <Stack
      spacing={3}
      sx={{
        px: 3,
        py: 4,
        maxWidth: 960,
        mx: 'auto',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: '100%',
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
            }}
          >
            {translate('tasks.MyDay.Header.Greeting', {userFirstName: user.firstName})}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            {translate('tasks.MyDay.Header.Summary', {todayDate: moment().format('dddd MMM DD'), numTasks: dueTodayCount})}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        spacing={1.5}
      >
        <Typography
          variant="overline"
          sx={{
            letterSpacing: '0.12em',
            fontSize: '16px',
            fontWeight: 700,
            color: 'grey.500',
          }}
        >
          {translate('tasks.MyDay.Sections.NeedsYou')}
        </Typography>
        <Stack
          spacing={1.5}
        >
          {
            isLoading && (
              <TaskListSkeleton />
            )
          }
          {
            !isLoading && todayTasksIncomplete.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                {translate('tasks.MyDay.Empty')}
              </Typography>
            )
          }
          {
            !isLoading && todayTasksIncomplete.map((task) => (
              <TaskItem
                key={task.taskRn}
                task={task}
                clientNameByRn={clientNameByRn}
                handleSetStatusToComplete={handleSetStatusToComplete}
                removeTask={edits.removeTask}
              />
            ))
          }
          {
            !isLoading && todayTasksComplete.map((task) => (
              <TaskItem
                key={task.taskRn}
                task={task}
                clientNameByRn={clientNameByRn}
                handleSetStatusToComplete={handleSetStatusToComplete}
                removeTask={edits.removeTask}
              />
            ))
          }
          <Box
            component='button'
            onClick={openDialogs.addTask}
            sx={{
              background: 'none',
              border: 'none',
              color: '#A8A49E',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '14px',
              p: 0,
              mt: 1,
            }}
          >
            <Box component='span'>+</Box>
            <Translate expression='tasks.Common.NewTask' />
          </Box>
        </Stack>
      </Stack>
      {popModelGenerator()}
    </Stack>
  );
};

MyDay.propTypes = {
  user: PropTypes.object,
  taskRefresh: PropTypes.number,
  taskRefreshBump: PropTypes.func.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  taskRefresh: state.taskRefresh?.value,
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(MyDay));
